import { CheckCircle2, AlertTriangle, ArrowRight, MousePointer, XCircle } from 'lucide-react';
import type { Campaign } from '@/types/sem';

interface LandingPageDiagnosisProps {
  campaigns: Campaign[];
}

interface DiagnosisResult {
  campaignName: string;
  adGroupName: string;
  ctr: number;
  convRate: number;
  spend: number;
  potentialRevenue: number;
  actualRevenue: number;
  lostRevenue: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function LandingPageDiagnosis({ campaigns }: LandingPageDiagnosisProps) {
  // Analyze all ad groups for landing page issues
  const landingPageIssues: DiagnosisResult[] = [];
  let totalAdGroups = 0;
  let healthyAdGroups = 0;
  let totalSpend = 0;
  let totalRevenue = 0;

  for (const campaign of campaigns) {
    if (campaign.metrics.spend <= 0) continue;
    for (const adGroup of campaign.adGroups) {
      if (adGroup.metrics.spend <= 0) continue;
      totalAdGroups++;
      totalSpend += adGroup.metrics.spend;
      totalRevenue += adGroup.metrics.conversionValue;

      const ctr = adGroup.metrics.ctr;
      const convRate = adGroup.metrics.clicks > 0
        ? (adGroup.metrics.conversions / adGroup.metrics.clicks) * 100
        : 0;

      // Good CTR (>= 1.5%) but bad conversion rate (< 2%) = landing page issue
      const hasGoodCtr = ctr >= 1.5;
      const hasBadConvRate = convRate < 2;

      if (hasGoodCtr && hasBadConvRate && adGroup.metrics.clicks > 50) {
        // Calculate potential revenue if conv rate was average (3%)
        const avgConvRate = 0.03;
        const potentialConversions = adGroup.metrics.clicks * avgConvRate;
        const avgConvValue = adGroup.metrics.conversions > 0
          ? adGroup.metrics.conversionValue / adGroup.metrics.conversions
          : 175; // Default conversion value
        const potentialRevenue = potentialConversions * avgConvValue;

        landingPageIssues.push({
          campaignName: campaign.name,
          adGroupName: adGroup.name,
          ctr,
          convRate,
          spend: adGroup.metrics.spend,
          potentialRevenue,
          actualRevenue: adGroup.metrics.conversionValue,
          lostRevenue: potentialRevenue - adGroup.metrics.conversionValue,
        });
      } else if (hasGoodCtr && !hasBadConvRate) {
        healthyAdGroups++;
      }
    }
  }

  // Calculate totals
  const totalLostRevenue = landingPageIssues.reduce((sum, issue) => sum + issue.lostRevenue, 0);
  const overallRoas = totalSpend > 0 ? totalRevenue / totalSpend : 0;

  // Extract unique sites from campaign names
  const affectedSites = [...new Set(landingPageIssues.map(issue => {
    const parts = issue.campaignName.split(' - ');
    return parts[0];
  }))];

  const hasIssues = landingPageIssues.length > 0;

  return (
    <div className="space-y-8">
      {/* Funnel Status - Compact horizontal */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
          Conversion Funnel Status
        </h3>

        <div className="flex flex-wrap items-center gap-3">
          {/* Ads Status */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--ed-green-bg)] border border-[rgba(74,186,122,0.12)]">
            <CheckCircle2 className="h-4 w-4 text-[var(--ed-green)]" />
            <span className="text-sm">Ads Working</span>
            <span className="text-xs text-muted-foreground ml-1 hidden sm:inline">{overallRoas.toFixed(2)}x ROAS</span>
          </div>

          <ArrowRight className="h-4 w-4 text-muted-foreground/30 hidden sm:block" />

          {/* Landing Page Status */}
          {hasIssues ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--ed-amber-bg)] border border-[rgba(224,168,82,0.12)]">
              <AlertTriangle className="h-4 w-4 text-[var(--ed-amber)]" />
              <span className="text-sm">LP Bottleneck</span>
              <span className="text-xs text-[var(--ed-amber)] ml-1">{landingPageIssues.length} issues</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--ed-green-bg)] border border-[rgba(74,186,122,0.12)]">
              <CheckCircle2 className="h-4 w-4 text-[var(--ed-green)]" />
              <span className="text-sm">Pages Converting</span>
            </div>
          )}

          <ArrowRight className="h-4 w-4 text-muted-foreground/30 hidden sm:block" />

          {/* Revenue */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/30 border border-border/30">
            <span className="text-sm font-medium">{formatCurrency(totalRevenue)}</span>
            <span className="text-xs text-muted-foreground">revenue</span>
          </div>

          {/* Healthy count */}
          <div className="sm:ml-auto text-xs text-muted-foreground">
            <span className="text-[var(--ed-green)] font-medium">{healthyAdGroups}</span> ad groups healthy
          </div>
        </div>
      </div>

      {/* Issues Table - Clean minimal design */}
      {hasIssues && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Landing Page Issues
            </h3>
            <div className="text-xs">
              <span className="text-muted-foreground">Est. opportunity: </span>
              <span className="text-[var(--ed-amber)] font-medium">{formatCurrency(totalLostRevenue)}</span>
            </div>
          </div>

          <div className="rounded-xl border border-border/30 overflow-hidden">
            {/* Table Header — hidden on mobile */}
            <div className="hidden sm:grid grid-cols-[1fr,80px,80px,100px] gap-4 px-4 py-2 bg-secondary/20 text-xs text-muted-foreground uppercase tracking-wider">
              <div>Campaign / Ad Group</div>
              <div className="text-right">CTR</div>
              <div className="text-right">Conv Rate</div>
              <div className="text-right">Lost Rev</div>
            </div>

            {/* Table Rows — stack on mobile */}
            <div className="divide-y divide-border/20">
              {landingPageIssues.slice(0, 5).map((issue, i) => (
                <div key={i} className="sm:grid sm:grid-cols-[1fr,80px,80px,100px] gap-4 px-4 py-3 items-center hover:bg-secondary/10 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{issue.campaignName}</p>
                    <p className="text-xs text-muted-foreground truncate">{issue.adGroupName}</p>
                  </div>

                  <div className="flex sm:justify-end items-center gap-3 sm:gap-1 mt-2 sm:mt-0">
                    <div className="flex items-center gap-1">
                      <MousePointer className="h-3 w-3 text-[var(--ed-green)]" />
                      <span className="text-sm text-[var(--ed-green)]">{formatPercent(issue.ctr)}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <XCircle className="h-3 w-3 text-[var(--ed-red)]" />
                      <span className="text-sm text-[var(--ed-red)]">{formatPercent(issue.convRate)}</span>
                    </div>

                    <span className="text-sm text-[var(--ed-amber)] sm:hidden">~{formatCurrency(issue.lostRevenue)}</span>
                  </div>

                  <div className="hidden sm:block text-right">
                    <div className="flex items-center justify-end gap-1">
                      <XCircle className="h-3 w-3 text-[var(--ed-red)]" />
                      <span className="text-sm text-[var(--ed-red)]">{formatPercent(issue.convRate)}</span>
                    </div>
                  </div>

                  <div className="hidden sm:block text-right">
                    <span className="text-sm text-[var(--ed-amber)]">~{formatCurrency(issue.lostRevenue)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Show more if needed */}
            {landingPageIssues.length > 5 && (
              <div className="px-4 py-2 bg-secondary/10 text-xs text-muted-foreground text-center">
                +{landingPageIssues.length - 5} more issues • {affectedSites.length} sites affected
              </div>
            )}
          </div>
        </div>
      )}

      {/* Key Insight - Subtle inline */}
      <div className="flex items-start gap-3 p-4 rounded-lg ed-card">
        <div className="w-1 h-full min-h-[40px] rounded-full bg-[var(--ed-t4)] shrink-0" />
        <div>
          <p className="text-xs font-medium text-[var(--ed-t2)] uppercase tracking-wider mb-1">Insight</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {hasIssues ? (
              <>
                Ads are driving qualified traffic — strong CTR and Quality Scores.
                The conversion drop-off occurs after the click, indicating landing page experience is the primary optimization opportunity.
                Improving LP conversion rates by 1% could generate <span className="text-[var(--ed-t1)] font-medium">{formatCurrency(totalLostRevenue * 0.5)}</span> additional revenue.
              </>
            ) : (
              <>
                All campaigns performing well with healthy conversion rates.
                Ad creative and landing pages working together effectively — {overallRoas.toFixed(2)}x ROAS.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
